import Enumerable from "linq";
import { ConfigurationType, OptionalColumnEnum, Style } from "../../common/enums/DRCXEnums";
import { DRCXAvailableColumns } from "../../common/models/drcx/DRCXAvailableColumns";
import { IDRCXAvailableColumnsDTO } from "../../common/models/drcx/DRCXAvailableColumnsDTO";
import { DRCXAvailableObjects } from "../../common/models/drcx/DRCXAvailableObjects";
import { DRCXAvailableObject } from "../../common/models/drcx/DRCXAvailableObject";
import { IDRCXAvailableObjectsDTO } from "../../common/models/drcx/DRCXAvailableObjectsDTO";
import { DRCXAvailableOptionalColumn } from "../../common/models/drcx/DRCXAvailableOptionalColumn";
import { IDRCXAvailableOptionalColumnDTO } from "../../common/models/drcx/DRCXAvailableOptionalColumnDTO";
import { IDRCXConfigurationDTO } from "../../common/models/drcx/DRCXConfigurationDTO";
import { DRCXConfigurationModel } from "../../common/models/drcx/DRCXConfigurationModel";
import OfflineDataService from "../offline-data-service/OfflineDataService";
import { GetAvailableColumnsOptions, GetAvailableObjectOptions, IDRCXConfigurationNameValidQueryParams } from "./Models";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { IDeviceType } from "../../common/models/IDeviceType";
import restService from "../rest-service/RestService";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";

const DRCXConfigurationDataService = {

    isConfigurationNameValid: async function (
        groupId: number,
        configurationType: ConfigurationType,
        configurationName: string,
        configurationId: number,
        signal?: AbortSignal
    ): Promise<boolean> {
        const path = `groups/${groupId}/drcxconfig/isvalidname`;

        const getParams: IDRCXConfigurationNameValidQueryParams = {
            configurationType: configurationType,
            configurationID: configurationId > 0 ? configurationId : undefined,
            configurationName: configurationName
        };

        const response = await restService.get<boolean, IDRCXConfigurationNameValidQueryParams>(path, signal, getParams);

        return response.data;
    },

    getConfiguration: async function (
        groupId: number,
        configurationId: number,
        signal?: AbortSignal
    ): Promise<DRCXConfigurationModel> {
        const path: string = `groups/${groupId}/drcxconfigs/${configurationId}`;

        const response = await restService.get<IDRCXConfigurationDTO>(path, signal);

        return new DRCXConfigurationModel(response.data);
    },

    saveConfiguration: async function (
        groupId: number,
        model: DRCXConfigurationModel,
        signal?: AbortSignal
    ): Promise<DRCXConfigurationModel> {
        
        // make sure attribute schema key is set correctly
        const configuration = {...model} as DRCXConfigurationModel;
        configuration.sections.forEach(o => {
            o.columns.filter(o => o.optionalColumn === OptionalColumnEnum.DeviceAttributeSchema ||
                                o.optionalColumn === OptionalColumnEnum.WellAttributeSchema ||
                                o.optionalColumn === OptionalColumnEnum.TankAttributeSchema ||
                                o.optionalColumn === OptionalColumnEnum.TagAttributeSchema).forEach(v => {
                                    if (v.id !== undefined) {
                                        v.key = v.id.toString();
                                    }
                                });
        });

        const path: string = `groups/${groupId}/drcxconfig`;

        const response = await restService.post<IDRCXConfigurationDTO, IDRCXConfigurationDTO>(path, model.toDTO(), signal);

        return new DRCXConfigurationModel({
            ...model.toDTO(),
            ...response.data
        });
    },

    deleteConfiguration: async function (
        groupId: number,
        model: DRCXConfigurationModel,
        signal?: AbortSignal
    ): Promise<void> {
        const path: string = `groups/${groupId}/drcxconfig`;
        
        await restService.delete<IDRCXConfigurationDTO, null>(path, signal, null, model.toDTO());
    },

    getAvailableObjects: async function (
        customerID: number,
        groupId: number,
        includeDescendentGroups: boolean = true,
        includeTankBatteries: boolean = false,
        offline: boolean,
        features?: ObjectFeaturesEnum,
        signal?: AbortSignal
    ): Promise<DRCXAvailableObjects> {

        if (!offline) {
            const path: string = `groups/${groupId}/drcxconfig/availableObjects`;

            const response = await restService.get<IDRCXAvailableObjectsDTO, GetAvailableObjectOptions>(
                path,
                signal,
                {
                    includeDevices: true,
                    includeTanks: true,
                    includeWells: true,
                    includeTankBatteries: includeTankBatteries,
                    includeDescendentGroups: includeDescendentGroups
                });

            return new DRCXAvailableObjects(response.data);
        } else {
            const p1 = OfflineDataService.getGroupDevices(customerID, groupId, true, features);
            const p2 = OfflineDataService.getGroupWells(customerID, groupId, features);
            const p3 = OfflineDataService.getGroupTankBatteries(customerID, groupId, features);

            let [devices, wells, tankBatteries] = await Promise.all([p1, p2, p3]);

            const availableData = new DRCXAvailableObjects({
                groups: [],
                devices: [],
                tankBatteries: [],
                tanks: [],
                wells: []
            });

            availableData.devices = devices.filter(o => o.deviceTypeID !== 1000)
                .map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.Device, o.deviceTypeID))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
            availableData.tanks = devices.filter(o => o.deviceTypeID === 1000)
                .map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.Tank))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
            availableData.wells = wells.map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.Well))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));
            availableData.tankBatteries = tankBatteries.map(o => new DRCXAvailableObject(o.name, o.id, eLynxObjectTypeEnum.TankBattery))
                .sort((a, b) => (a.displayText as string).localeCompare(b.displayText as string));

            return availableData;
        }
    },

    getAvailableColumns: async function (
        groupId: number,
        objects: Array<IeLynxObject>,
        dataType: eLynxObjectTypeEnum,
        signal?: AbortSignal
    ): Promise<DRCXAvailableColumns> {

        var path: string = `groups/${groupId}/drcxconfig/availableColumns`;

        const response = await restService
            .post<GetAvailableColumnsOptions, IDRCXAvailableColumnsDTO>(
                path,
                {
                    objects: objects.map(o => {
                        return {
                            id: o.id,
                            objectType: o.objectType
                        };
                    }),
                    dataType: dataType
                }
                ,
                signal);

        return new DRCXAvailableColumns(response.data);
    },

    getOptionalColumns: async function (
        groupId: number,
        signal?: AbortSignal
    ): Promise<Enumerable.ILookup<string, DRCXAvailableOptionalColumn> | undefined> {
        var path: string = `groups/${groupId}/drcxconfig/optionalColumns`;

        const response = await restService.get<Array<IDRCXAvailableOptionalColumnDTO>>(path, signal);

        return response.data
            ? Enumerable.from(response.data).toLookup(
                d => DRCXAvailableOptionalColumn.getLookupKey(d.ds ?? Style.Linear, d.ot ?? eLynxObjectTypeEnum.None),
                // Note: Setting key = d.dt instead of d.ky is intentional. This enables custom labels to work.
                d => new DRCXAvailableOptionalColumn(d.dt, d.oc, d.dt, d.ut, d.do, undefined, d.id))
            : undefined;
    },

    getAvailableDeviceTypes: async function (
        groupId: number,
        signal?: AbortSignal
    ): Promise<IDeviceType[]> {
        var path: string = `groups/${groupId}/drcxconfig/availableDeviceTypes`;

        const response = await restService.get<IDeviceType[]>(path, signal);

        return response.data;
    }
};

export default DRCXConfigurationDataService;